import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Grid, Paper, IconButton, Popover, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import { colors } from '../../theme/colors';

// Generate time slots from 5 AM to 8 PM
const generateTimeSlots = (startHour = 5, endHour = 20) => {
  const slots = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    const period = hour < 12 ? 'AM' : 'PM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    slots.push(`${displayHour}:00 ${period}`);
  }
  return slots;
};

const startTimeSlots = generateTimeSlots(5, 20); // 5 AM to 8 PM
const endTimeSlots = generateTimeSlots(8, 20);  // 8 AM to 8 PM

const TimeSlot = styled(Paper)(({ isSelected, isDisabled }) => ({
  padding: '10px',
  textAlign: 'center',
  backgroundColor: isSelected ? colors.primary : colors.mediumBackground,
  color: colors.lightText,
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  borderRadius: '4px',
  opacity: isDisabled ? 0.5 : 1,
  fontSize: '13px',
  '&:hover': {
    backgroundColor: isDisabled ? `${colors.mediumBackground}80` : isSelected ? colors.primary : colors.lightBackground,
    transform: isDisabled ? 'none' : 'scale(1.02)',
  }
}));

const TimeField = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  backgroundColor: colors.mediumBackground,
  borderRadius: '6px',
  marginBottom: '12px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colors.lightBackground,
  }
});

const TimeSelector = ({ 
  selectedStartTime = '5:00 AM', 
  selectedEndTime = '6:00 PM', 
  onStartTimeChange, 
  onEndTimeChange,
  onReset  // Make sure this prop is defined
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [timeType, setTimeType] = useState(null); // 'start' or 'end'

  // Handle time field click
  const handleTimeFieldClick = (event, type) => {
    setAnchorEl(event.currentTarget);
    setTimeType(type);
  };

  // Handle time selection
  const handleTimeSelect = (time) => {
    if (timeType === 'start') {
      onStartTimeChange(time);
      
      // Auto-validate end time
      const startHour = parseInt(time.split(':')[0]) + (time.includes('PM') && !time.includes('12:') ? 12 : 0);
      const endHour = parseInt(selectedEndTime.split(':')[0]) + (selectedEndTime.includes('PM') && !selectedEndTime.includes('12:') ? 12 : 0);
      
      if (startHour >= endHour) {
        // Find next available end time (at least 1 hour after start)
        const nextValidEndTime = startTimeSlots.find((t) => {
          const hour = parseInt(t.split(':')[0]) + (t.includes('PM') && !t.includes('12:') ? 12 : 0);
          return hour > startHour;
        });
        
        if (nextValidEndTime) {
          onEndTimeChange(nextValidEndTime);
        }
      }
      
      // Automatically open end time selector after selecting start time
      setTimeout(() => {
        setTimeType('end');
      }, 300);
    } else {
      onEndTimeChange(time);
      setAnchorEl(null); // Close popover after end time selection
    }
  };

  // Close popover
  const handleClose = () => {
    setAnchorEl(null);
    setTimeType(null);
  };

  // Check if end time is disabled
  const isEndTimeDisabled = (time) => {
    const startHour = parseInt(selectedStartTime.split(':')[0]) + (selectedStartTime.includes('PM') && !selectedStartTime.includes('12:') ? 12 : 0);
    const endHour = parseInt(time.split(':')[0]) + (time.includes('PM') && !time.includes('12:') ? 12 : 0);
    return endHour <= startHour;
  };

  // Make sure this function is defined
  const handleReset = () => {
    if (onReset) {
      onReset();
    }
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ mt: 3, backgroundColor: colors.darkBackground, borderRadius: '8px', p: 2, mb:2 }}>
      {/* Start Time Field */}
      <TimeField onClick={(e) => handleTimeFieldClick(e, 'start')}>
        <AccessTimeIcon sx={{ color: colors.primary, mr: 1, fontSize: 20 }} />
        <Typography sx={{ fontSize: '14px' }}>
          Start Time : {selectedStartTime}
        </Typography>
      </TimeField>

      {/* End Time Field */}
      <TimeField onClick={(e) => handleTimeFieldClick(e, 'end')}>
        <AccessTimeIcon sx={{ color: colors.primary, mr: 1, fontSize: 20 }} />
        <Typography sx={{ fontSize: '14px' }}>
          End Time : {selectedEndTime}
        </Typography>
      </TimeField>

      {/* Reset Button - THIS NEEDS TO BE RENDERED */}
      {/* <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        mt: 2,
        pt: 2,
        borderTop: `1px solid ${colors.border}`
      }}>
        <Button 
          variant="contained" 
          startIcon={<RefreshIcon />}
          onClick={handleReset}
          sx={{
            backgroundColor: colors.primary,
            color: '#fff',
            fontSize: '14px',
            padding: '8px 16px',
            '&:hover': {
              backgroundColor: '#e04433',
            }
          }}
        >
          Reset Date & Time
        </Button>
      </Box> */}

      {/* Time Selection Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: { 
            backgroundColor: colors.darkBackground,
            borderRadius: '8px',
            boxShadow: '0 6px 12px rgba(0,0,0,0.2)',
            mt: 1,
            p: 2,
            maxWidth: '300px'
          }
        }}
      >
        <Grid container spacing={1}>
          {(timeType === 'start' ? startTimeSlots : endTimeSlots).map((time) => (
            <Grid item xs={4} key={`${timeType}-${time}`}>
              <TimeSlot 
                isSelected={(timeType === 'start' ? selectedStartTime : selectedEndTime) === time}
                isDisabled={timeType === 'end' && isEndTimeDisabled(time)}
                onClick={() => !isEndTimeDisabled(time) && handleTimeSelect(time)}
              >
                {time}
              </TimeSlot>
            </Grid>
          ))}
        </Grid>
      </Popover>
    </Box>
  );
};

export default TimeSelector; 